import {
  ApartmentOutlined, AuditOutlined, BarChartOutlined, CalendarOutlined, CheckCircleOutlined, ControlOutlined,
  DollarOutlined, EnvironmentOutlined, FileTextOutlined, HomeOutlined, MenuFoldOutlined, MenuOutlined,
  MenuUnfoldOutlined, SettingOutlined, StarOutlined, TagsOutlined, TeamOutlined, ToolOutlined,
  TruckOutlined, UserOutlined
} from '@ant-design/icons';
import { Button, Drawer, Layout, Menu, Space, Typography } from 'antd';
import { useEffect, useLayoutEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth.js';
import { selectedNavigation, visibleNavigation } from './navigation.js';

const sidebarPreferenceKey = 'after-sales-erp-sidebar-collapsed';
const desktopMediaQuery = '(min-width: 992px)';
const wideDesktopMediaQuery = '(min-width: 1200px)';
const icons: Record<string, React.ReactNode> = {
  '/': <HomeOutlined />, business: <FileTextOutlined />, '/work-orders': <FileTextOutlined />,
  '/task-annotation': <TagsOutlined />, '/dispatch-center': <ApartmentOutlined />,
  '/task-reviews': <AuditOutlined />, '/evaluations': <StarOutlined />,
  fulfillment: <TruckOutlined />, '/visits': <CalendarOutlined />, '/logistics': <ToolOutlined />,
  '/dashboard': <BarChartOutlined />, bonus: <DollarOutlined />,
  '/bonus/rules': <SettingOutlined />, '/bonus/preview': <BarChartOutlined />,
  '/bonus/settlement': <CheckCircleOutlined />, rules: <ControlOutlined />,
  '/settings/dispatch-roles': <TeamOutlined />, '/settings/task-labels': <TagsOutlined />,
  '/settings/service-centers': <ApartmentOutlined />, '/settings/onsite-coverages': <EnvironmentOutlined />,
  system: <SettingOutlined />, '/settings/accounts': <UserOutlined />,
  '/settings/master-data': <FileTextOutlined />, '/settings/map-provider': <EnvironmentOutlined />
};

function Brand({ compact }: { compact: boolean }) {
  return <div className={`brand${compact ? ' brand-compact' : ''}`} aria-label="售后 ERP">
    <span className="brand-mark" aria-hidden="true">售</span>
    {!compact ? <span className="brand-name">售后 ERP</span> : null}
  </div>;
}

function useMediaMatch(query: string) {
  const [matches, setMatches] = useState(() => typeof window !== 'undefined' && window.matchMedia(query).matches);
  useEffect(() => {
    const media = window.matchMedia(query);
    const update = () => setMatches(media.matches);
    update();
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, [query]);
  return matches;
}

export function AppShell() {
  const location = useLocation();
  const navigate = useNavigate();
  const auth = useAuth();
  const desktop = useMediaMatch(desktopMediaQuery);
  const wideDesktop = useMediaMatch(wideDesktopMediaQuery);
  const [menuOpen, setMenuOpen] = useState(false);
  const [openKeys, setOpenKeys] = useState<string[]>([]);
  const [savedCollapse, setSavedCollapse] = useState<boolean | null>(() => {
    try {
      const value = localStorage.getItem(sidebarPreferenceKey);
      return value === null ? null : value === 'true';
    } catch { return null; }
  });
  const collapsed = savedCollapse ?? !wideDesktop;
  const visible = visibleNavigation(auth.can, Boolean(auth.account?.isPlatformAdmin));
  const selected = selectedNavigation(location.pathname, visible);
  useEffect(() => { setOpenKeys(selected.group ? [selected.group] : []); }, [selected.group]);
  useLayoutEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [location.pathname]);

  const menuItems = visible.map((item) => ({
    key: item.key, label: item.label, icon: icons[item.key],
    children: item.children?.map((child) => ({ key: child.key, label: child.label, icon: icons[child.key] }))
  }));
  const navMenu = (compact: boolean) => <Menu className="app-nav-menu" mode="inline" theme="dark" items={menuItems}
    selectedKeys={[selected.key]} inlineCollapsed={compact}
    {...(compact ? {} : { openKeys })} onOpenChange={(keys) => setOpenKeys(keys.slice(-1))}
    onClick={({ key }) => { setMenuOpen(false); void navigate(key); }} />;
  const toggleCollapse = () => {
    const next = !collapsed;
    setSavedCollapse(next);
    try { localStorage.setItem(sidebarPreferenceKey, String(next)); } catch { /* Navigation remains usable without storage. */ }
  };

  return <Layout className="app-layout">
    {desktop ? <Layout.Sider width={220} collapsedWidth={72} collapsed={collapsed} trigger={null} className="app-sider">
      <Brand compact={collapsed} />
      {navMenu(collapsed)}
      <Button className="app-sidebar-toggle" type="text" icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
        aria-label={collapsed ? '展开侧栏' : '收起侧栏'} onClick={toggleCollapse}>
        {collapsed ? null : '收起侧栏'}
      </Button>
    </Layout.Sider> : <Drawer className="mobile-nav" width={260} placement="left" open={menuOpen}
      onClose={() => setMenuOpen(false)} closable={false}>
      <Brand compact={false} />{navMenu(false)}
    </Drawer>}
    <Layout>
      <Layout.Header className="app-header">
        <Space className="app-header-brand">
          {!desktop ? <Button aria-label="打开导航" icon={<MenuOutlined />} onClick={() => setMenuOpen(true)} /> : null}
          <Typography.Text strong>全国售后服务运营平台</Typography.Text>
        </Space>
        <Space className="app-header-account">
          <Typography.Text type="secondary">{auth.account?.displayName}</Typography.Text>
          <Button size="small" onClick={() => void auth.logout()}>退出</Button>
        </Space>
      </Layout.Header>
      <Layout.Content className="app-content">
        <div className="app-main-frame"><Outlet /></div>
      </Layout.Content>
    </Layout>
  </Layout>;
}
